import clsx from 'clsx'
import { FC, useCallback } from 'react'
import { EMPTY, from } from 'rxjs'
import { catchError, finalize, switchMap, takeUntil, tap } from 'rxjs/operators'
import { ProfileApi } from 'src/api'
import { Button } from 'src/components'
import { useBehaviorMapper, useUnsubscribe } from 'src/hooks'
import { IconLikeStroke, IconTalent, IconVideoMulti } from 'src/icons'
import { RecruiterOnboardingImg } from 'src/images'
import { ModalRecorder } from 'src/partials/recorder/modal-recorder'
import { browserHistory, ERoutes } from 'src/router'
import { generate } from 'src/router/generate'
import { DialogService, SnackbarService } from 'src/services'
import { AuthModule, LoadingModule } from 'src/store'
import Style from './style.module.scss'

export const Onboarding: FC = () => {
  const unsubscribe$ = useUnsubscribe()
  const isCompanyVerified = useBehaviorMapper(AuthModule.verifiedCompany$)

  const onUpdateProfile = useCallback((pfvId: number) => {
    LoadingModule.toggle(true)
    from(ProfileApi.update({ pfvId }))
      .pipe(
        switchMap(() => ProfileApi.getProfile()),
        tap(({ data }) => AuthModule.updateProfile(data)),
        takeUntil(unsubscribe$),
        catchError((error) => {
          SnackbarService.axiosError(error)
          return EMPTY
        }),
        finalize(() => LoadingModule.toggle(false))
      )
      .subscribe(() => {
        browserHistory.push(generate(ERoutes.MY_VIDEOS))
      })
  }, [unsubscribe$])

  return (
    <div className="fx-1 fx bg-neutral-white h-full">
      <div className="fx-1 fx-column fx-center p-10">
        <div className="fx-column gap-8" style={{ width: 'min(520px, 100%)' }}>
          <div className="fx-column gap-4">
            <div className="semibold-36 txt-black-02">Preview Talent Vibe Checks</div>
            <div className="body-18 txt-black-02">Watch short video intros and get a feel for each candidate's energy and style</div>
          </div>

          <div className="fx-column gap-4">
            <div className="semibold-36 txt-black-02">Like Talent. Get Liked Back.</div>
            <div className="body-18 txt-black-02">If the feeling's mutual, you can message one another</div>
          </div>

          <div className="fx-column gap-4">
            <div className="semibold-36 txt-black-02">Why Vibe Checks Work</div>
            <div className="body-18 txt-black-02">3x faster placements, 40% higher retention. Real human trust.</div>
          </div>
        </div>
      </div>

      <div className={clsx('fx-column fx-center gap-5', Style.gradientBg)}>
        <img src={RecruiterOnboardingImg} alt="recruiter-onboarding"/>
        <div className="fx-column gap-3 relative">
          <Button
            variant="primary"
            className="fx gap-2"
            onClick={() => DialogService.open(ModalRecorder, {
              forProfile: true,
              requireCompanyVerified: !isCompanyVerified,
              onClose: (result) => result?.video?.id && onUpdateProfile?.(result?.video?.id)
            })}
          >
            <IconVideoMulti/>
            Record Your VVC
          </Button>
          <Button
            variant="secondary"
            className="fx gap-2"
            onClick={() => browserHistory.push(generate(ERoutes.TALENT_VIBE_TALENT))}
          >
            <IconTalent className="svg-current-color"/>
            Vibe Talent
          </Button>

          <IconLikeStroke className={Style.iconLike}/>
          <IconLikeStroke size={44} className={Style.iconLikeSmall}/>
        </div>
      </div>
    </div>
  )
}
